import * as THREE from 'three';

/**
 * Procedurally generated 33^3 colour grading LUT (Data3DTexture, RGBA8).
 *
 * No external .cube file — the grade is *computed*, which means it can be
 * re-authored per time-of-day and stays inside the "no external assets" rule.
 *
 * The transform is a real colourist chain applied in display-referred space:
 *   ASC-CDL slope/offset/power per channel  ->  split tone (cool shadows,
 *   warm highlights, the modern shooter signature)  ->  luminance-preserving
 *   saturation  ->  highlight desaturation (film shoulder behaviour)  ->
 *   a filmic S-curve pivoted on 18% grey.
 */

const SIZE = 33;

function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
function linearToSrgb(c) {
  c = Math.max(0, c);
  return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}
const LUM = [0.2126, 0.7152, 0.0722];

export const GRADE_PRESETS = {
  /**
   * STEEL CITY — the shipping grade.
   *
   * DESIGN.md: sodium-lamp amber, molten slag orange, river teal,
   * cold steel, over a wet grey-brown rustbelt base — "not Los Santos pastel...
   * a colder, heavier, more industrial city, and the contrast with GTA V's
   * palette is the point of difference".
   *
   * Which means this grade has to do two things at once that pull against each
   * other: be as filmic as a Rockstar frame, and read COLDER than one. The four
   * decisions that get there:
   *
   *  1. THE SPLIT IS TEAL/AMBER, NOT BLUE/ORANGE. The shadow tint carries green
   *     with its blue (river teal, oxidised steel) rather than pure blue, which
   *     is what stops the frame reading as "night filter" and starts it reading
   *     as wet metal. The highlight tint is sodium — amber with a little blue
   *     subtracted — so a lamp, a slag glow and a low sun all land on the same
   *     warm axis and everything in shade lands on the opposite one.
   *  2. THE BLACKS ARE CRUSHED, SLIGHTLY. A negative offset on all three
   *     channels plus a 0.006 toe: two code values of atmosphere in the
   *     darkest corner instead of a true zero, and nothing above it lifted.
   *     GTA V's shadows are dense; a lifted toe is what makes a procedural
   *     frame look like a WebGL demo.
   *  3. SATURATION IS PUSHED HARD BUT SELECTIVELY. AgX's inset/outset pair is
   *     a desaturating transform by construction and its shoulder takes another
   *     bite, so anything under ~1.2 here comes out pastel. 1.26 restores it.
   *  4. ...AND THE GREEN-YELLOW BAND IS PULLED BACK OUT (see `greenTrim`).
   *     This is the single most Los-Santos-specific thing in a default grade
   *     and the easiest to get wrong: lush green foliage and warm yellow stucco
   *     are what a California basin looks like. A Pittsburgh river valley's
   *     vegetation is dark and grey-green and its brick is brown, so that one
   *     band is desaturated while red-orange and cyan-blue are left alone.
   */
  steelcity: {
    // Cool the base: red down a touch, blue up. Small numbers — the split tone
    // below does the visible work and this only sets which way neutral leans.
    slope: [0.984, 0.996, 1.012],
    // Crushed blacks. Least in blue, so what remains down there is cool rather
    // than muddy.
    // Eased back from (-0.011, -0.007, +0.001). A critic panel measured 11.7%
    // of a sunset frame at or below code value 4 with green and blue floored
    // flat — an offset that big subtracts more than the input in the bottom
    // three code values, so the channels clip to zero at different inputs and
    // the hue in shade is destroyed before the toe ever gets to lift it.
    // "Slightly crushed" is a shoulder on the toe, not a subtraction.
    offset: [-0.005, -0.003, 0.002],
    power: [1.02, 1.005, 0.994],
    // River teal / cold steel in the shadows.
    shadowTint: [-0.007, 0.009, 0.024],
    // Sodium amber in the highlights.
    highlightTint: [0.038, 0.017, -0.013],
    saturation: 1.26,
    contrast: 1.32,
    pivot: 0.50,
    highlightDesat: 0.12,
    // 3 code values of atmosphere in the darkest corner rather than 1.5. Deep
    // shade in a city is lit by something — sky, a wall opposite, a lamp — and
    // a frame with a tenth of its pixels at absolute zero has thrown that away.
    toe: 0.012,
    shoulder: 0.60,
    shoulderSoft: 1.20,
    /**
     * Hue-selective desaturation of the green-yellow band, 0..1. See decision 4
     * above. 0.30 takes a third of the chroma out of foliage and yellow-green
     * plaster while leaving slag orange, sodium amber, river teal and the
     * DeCarlo brothers' own colours (#ff6a12, #2ea6a0, #c07cff) untouched.
     */
    greenTrim: 0.30,
    /** Centre and width of that band, in normalised hue. 0.24 is yellow-green. */
    greenHue: 0.24,
    greenWidth: 0.10,
  },

  // The inherited neutral-but-cinematic grade, kept as the A/B reference:
  // slightly cool shadows, warm highlights, a touch of contrast, mild highlight
  // desaturation. `createGradeLut('default')` still returns it.
  default: {
    slope: [1.0, 0.995, 0.985],
    offset: [-0.004, -0.002, 0.004],
    power: [1.0, 1.005, 1.02],
    shadowTint: [-0.001, 0.006, 0.022],
    highlightTint: [0.030, 0.014, -0.006],
    // Display-space saturation. It has to be well over unity because AgX's
    // inset/outset pair is a *desaturating* transform by construction and the
    // shoulder takes another chunk out of anything bright: measured on the
    // 16:30 frame the zenith came out of the tone map at B-R = +15 code values
    // for a sky whose scene radiance is 3:1 blue over red.
    saturation: 1.20,
    // Global contrast, as a power about a pivot.
    //
    // The pivot is the CODE VALUE that must not move, and it has to sit at
    // mid-grey or the "contrast" is really an exposure lift: at 0.42 (code 107)
    // everything above a fairly dark mid-tone got brighter, which is half of
    // why 18% scene grey was landing on code 153 instead of ~120 and the whole
    // frame read as a milky pastel wash with no blacks in it. AgX puts 18%
    // scene grey near 0.50 display, so that is where the pivot belongs.
    contrast: 1.28,
    pivot: 0.50,
    // Film loses chroma in the shoulder, but 0.28 on top of AgX's own
    // desaturation is what turned the sunset into a cream void and the noon
    // zenith into grey. A tenth is enough to keep a specular hit from going
    // neon without bleaching the sky.
    highlightDesat: 0.10,
    // Toe lift, in code values / 255. A modern shooter frame does keep a couple
    // of code values of atmosphere in its darkest corner rather than a true
    // zero — but 0.017 (four and a half codes) added to a curve that was
    // already lifting the low mids is a grey veil over the bottom third of the
    // histogram. 0.008 is two codes: visible as "not a hole", invisible as haze.
    toe: 0.008,
    // Shoulder knee, in code values. The grade runs in display space, so the
    // contrast power routinely pushes 0.8 up past 1.0 and *something* has to
    // roll it off; an exponential approach to 1.0 from this knee keeps cloud
    // and specular separation that a hard divide-by would flatten to 3 codes.
    shoulder: 0.60,
    // Shoulder softness, in the SAME post-contrast units as the knee. The
    // exponential is divided through by its own value at the largest output the
    // contrast power can produce — pivot * (1/pivot)^contrast — so that
    //   scurve(1) == 1 EXACTLY
    // and display white is actually reachable.
    //
    // This is the fix for "p99.9 = 230, nothing above 88% luminance, milky
    // pastel wash". The shoulder used to be UNNORMALISED with a knee of 0.66 and
    // a softness of (1 - k), so input 1.0 landed on
    //   0.66 + 0.34 * (1 - e^-1) = 0.875  ->  223 code values,
    // and the sky, the sunlit stucco and every specular highlight piled up inside
    // 220..232. The knee comes DOWN rather than up, with a much longer softness:
    // that reproduces the old curve to within 3 code values everywhere below
    // 0.83 — the mid-tones were never the problem — while the last fifth
    // continues to 255 instead of asymptoting 32 codes short of it.
    shoulderSoft: 1.20,
  },
};

/**
 * Shoulder constants for a preset: knee, softness, and the normaliser that
 * makes the curve land on 1.0 at input 1.0.
 */
function shoulderParams(g) {
  const k = Math.min(0.98, Math.max(0.05, g.shoulder));
  const s = Math.max(1e-3, g.shoulderSoft ?? 0.55 * (1 - k));
  // Largest post-contrast value an in-gamut input (1.0) can produce.
  const cMax = g.pivot * Math.pow(1 / g.pivot, g.contrast);
  const norm = 1 - Math.exp(-Math.max(cMax - k, 1e-3) / s);
  return { k, s, norm };
}

function applyGrade(rgb, g, sh) {
  let r = rgb[0];
  let gg = rgb[1];
  let b = rgb[2];

  // ASC CDL: slope / offset / power
  r = Math.pow(Math.max(0, r * g.slope[0] + g.offset[0]), g.power[0]);
  gg = Math.pow(Math.max(0, gg * g.slope[1] + g.offset[1]), g.power[1]);
  b = Math.pow(Math.max(0, b * g.slope[2] + g.offset[2]), g.power[2]);

  // split toning by luminance
  const l = r * LUM[0] + gg * LUM[1] + b * LUM[2];
  const shadowW = Math.pow(1 - Math.min(1, l), 2.2);
  const highW = Math.pow(Math.min(1, l), 2.0);
  r += g.shadowTint[0] * shadowW + g.highlightTint[0] * highW;
  gg += g.shadowTint[1] * shadowW + g.highlightTint[1] * highW;
  b += g.shadowTint[2] * shadowW + g.highlightTint[2] * highW;

  // saturation about luminance
  const l2 = r * LUM[0] + gg * LUM[1] + b * LUM[2];
  r = l2 + (r - l2) * g.saturation;
  gg = l2 + (gg - l2) * g.saturation;
  b = l2 + (b - l2) * g.saturation;

  // highlights lose saturation the way film does
  const hd = g.highlightDesat * Math.pow(Math.min(1, Math.max(0, l2)), 3.0);
  r += (l2 - r) * hd;
  gg += (l2 - gg) * hd;
  b += (l2 - b) * hd;

  // --- hue-selective trim on the green-yellow band -------------------------
  // A global saturation push is the right tool for "filmic", and the wrong one
  // for "not Los Santos": it lifts the foliage and the yellow stucco just as
  // hard as the sodium and the teal, and those two bands are precisely what
  // makes a frame read as a warm California basin. Pulling one hue band back
  // toward its own luminance is a colourist's secondary, done here because the
  // LUT is built on the CPU and it therefore costs nothing at runtime.
  if (g.greenTrim > 0) {
    const mx = Math.max(r, gg, b);
    const mn = Math.min(r, gg, b);
    const c = mx - mn;
    if (c > 1e-4) {
      let hue;
      if (mx === r) hue = ((gg - b) / c + 6) % 6;
      else if (mx === gg) hue = (b - r) / c + 2;
      else hue = (r - gg) / c + 4;
      hue /= 6;
      // wrap-aware distance to the band centre
      let dh = Math.abs(hue - g.greenHue);
      if (dh > 0.5) dh = 1 - dh;
      const w = Math.exp(-(dh * dh) / (2 * g.greenWidth * g.greenWidth));
      const k = 1 - g.greenTrim * w;
      const l3 = r * LUM[0] + gg * LUM[1] + b * LUM[2];
      r = l3 + (r - l3) * k;
      gg = l3 + (gg - l3) * k;
      b = l3 + (b - l3) * k;
    }
  }

  // filmic S-curve around the pivot, with a lifted toe for that "there is
  // atmosphere in the shadows" look rather than crushed black
  const scurve = (x) => {
    const t = Math.max(0, x);
    // contrast as a power about the pivot: the pivot itself never moves
    let c = t <= 0 ? 0 : g.pivot * Math.pow(t / g.pivot, g.contrast);
    // Soft shoulder so the top end rolls off instead of clipping flat, but
    // NORMALISED so that the shoulder still arrives at 1.0. Without the
    // division by `sh.norm` the curve asymptotes short of white.
    if (c > sh.k) {
      c = sh.k + (1 - sh.k) * ((1 - Math.exp(-(c - sh.k) / sh.s)) / sh.norm);
    }
    return g.toe + (1 - g.toe) * Math.min(1, Math.max(0, c));
  };
  return [scurve(r), scurve(gg), scurve(b)];
}

export function createGradeLut(preset = 'steelcity') {
  const g = GRADE_PRESETS[preset] ?? GRADE_PRESETS.steelcity;
  const sh = shoulderParams(g);
  const n = SIZE;
  const data = new Uint8Array(n * n * n * 4);
  let p = 0;
  for (let z = 0; z < n; z++) {
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const inp = [x / (n - 1), y / (n - 1), z / (n - 1)];
        const out = applyGrade(inp, g, sh);
        data[p++] = Math.round(Math.min(1, Math.max(0, out[0])) * 255);
        data[p++] = Math.round(Math.min(1, Math.max(0, out[1])) * 255);
        data[p++] = Math.round(Math.min(1, Math.max(0, out[2])) * 255);
        data[p++] = 255;
      }
    }
  }
  const tex = new THREE.Data3DTexture(data, n, n, n);
  tex.format = THREE.RGBAFormat;
  tex.type = THREE.UnsignedByteType;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.wrapR = THREE.ClampToEdgeWrapping;
  tex.unpackAlignment = 1;
  tex.needsUpdate = true;
  tex.name = 'ow-grade-lut';
  return { texture: tex, size: n };
}

export { srgbToLinear, linearToSrgb };
